// pages/EmailService.js
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Chip,
  IconButton,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Email,
  Send,
  AttachFile,
  CheckCircle,
  Schedule,
  Person,
  Phone,
  Business,
  PriorityHigh,
  CloudUpload,
  Delete
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const EmailService = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    priority: 'normal',
    attachments: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Contact Info', 'Message Details', 'Review & Send'];

  const priorities = [
    { value: 'low', label: 'Low', color: 'default' },
    { value: 'normal', label: 'Normal', color: 'primary' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'error' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.name || !formData.email) {
        setErrors({
          name: !formData.name ? 'Name is required' : '',
          email: !formData.email ? 'Email is required' : ''
        });
        return;
      }
    } else if (activeStep === 1) {
      if (!formData.subject || !formData.message) {
        setErrors({
          subject: !formData.subject ? 'Subject is required' : '',
          message: !formData.message ? 'Message is required' : ''
        });
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate email sending
    try {
      // In a real application, you would integrate with an email service like EmailJS, SendGrid, etc.
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create mailto link as fallback
      const subject = encodeURIComponent(formData.subject);
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nCompany: ${formData.company}\n\nMessage:\n${formData.message}`
      );
      
      const mailtoLink = `mailto:contact.in.dinesh@gmail.com?subject=${subject}&body=${body}`;
      window.open(mailtoLink, '_blank');
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        priority: 'normal',
        attachments: []
      });
      setActiveStep(0);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 50%, #0a1128 100%)',
        py: 8,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)',
        }}
      />

      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  mb: 2,
                  background: 'linear-gradient(135deg, #8b0000, #ff4d4d, #ff9999)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Contact Us
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 300,
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                }}
              >
                Get in touch with Dinesh Muthaiyan for project inquiries, collaborations, or any questions
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {/* Contact Info Card */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(139, 0, 0, 0.3)',
                    borderRadius: 4,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          background: 'linear-gradient(135deg, #8b0000, #ff4d4d)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <Email sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          mb: 1,
                        }}
                      >
                        Dinesh Muthaiyan
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'rgba(255,255,255,0.7)',
                          mb: 3,
                        }}
                      >
                        Founder & CEO
                      </Typography>
                    </Box>

                    <Box sx={{ space: 2 }}>
                      {[
                        {
                          icon: <Email sx={{ fontSize: 20 }} />,
                          label: 'Email',
                          value: 'dinesh@zolangnexora.com',
                          color: '#ff4d4d'
                        },
                        {
                          icon: <Phone sx={{ fontSize: 20 }} />,
                          label: 'Phone',
                          value: '+91 6382585971',
                          color: '#4CAF50'
                        },
                        {
                          icon: <Business sx={{ fontSize: 20 }} />,
                          label: 'Company',
                          value: 'Zolang Nexora Softwares',
                          color: '#2196F3'
                        }
                      ].map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                            p: 2,
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.1)',
                          }}
                        >
                          <Box
                            sx={{
                              color: item.color,
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.6)',
                                display: 'block',
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'white',
                                fontWeight: 500,
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 4, p: 3, background: 'rgba(139, 0, 0, 0.1)', borderRadius: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255,255,255,0.8)',
                          textAlign: 'center',
                          fontStyle: 'italic',
                        }}
                      >
                        "Let's build something amazing together. I typically respond within 24 hours."
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Email Form */}
            <Grid item xs={12} md={8}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(139, 0, 0, 0.3)',
                    borderRadius: 4,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Stepper */}
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel
                            sx={{
                              '& .MuiStepLabel-label': {
                                color: 'rgba(255,255,255,0.7)',
                                '&.Mui-completed': {
                                  color: '#4CAF50',
                                },
                                '&.Mui-active': {
                                  color: '#ff4d4d',
                                },
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>

                    {/* Status Alerts */}
                    {submitStatus === 'success' && (
                      <Alert severity="success" sx={{ mb: 3 }}>
                        Message sent successfully! You should receive a response within 24 hours.
                      </Alert>
                    )}
                    {submitStatus === 'error' && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        There was an error sending your message. Please try again or contact directly.
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* Step 1: Contact Information */}
                      {activeStep === 0 && (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Your Name"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Person sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                      borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff4d4d',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                  },
                                  '& .MuiFormHelperText-root': {
                                    color: '#ff4d4d',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                error={!!errors.email}
                                helperText={errors.email}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Email sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                      borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff4d4d',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                  },
                                  '& .MuiFormHelperText-root': {
                                    color: '#ff4d4d',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Phone Number"
                                value={formData.phone}
                                onChange={handleInputChange('phone')}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Phone sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                      borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff4d4d',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Company"
                                value={formData.company}
                                onChange={handleInputChange('company')}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Business sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                      borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff4d4d',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </motion.div>
                      )}

                      {/* Step 2: Message Details */}
                      {activeStep === 1 && (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Subject"
                                value={formData.subject}
                                onChange={handleInputChange('subject')}
                                error={!!errors.subject}
                                helperText={errors.subject}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                      borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff4d4d',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                  },
                                  '& .MuiFormHelperText-root': {
                                    color: '#ff4d4d',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                                  Priority
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {priorities.map((priority) => (
                                    <Chip
                                      key={priority.value}
                                      label={priority.label}
                                      clickable
                                      color={priority.color}
                                      variant={formData.priority === priority.value ? 'filled' : 'outlined'}
                                      onClick={() => setFormData(prev => ({ ...prev, priority: priority.value }))}
                                      sx={{
                                        borderColor: formData.priority === priority.value ? 'transparent' : 'rgba(255,255,255,0.3)',
                                        color: formData.priority === priority.value ? 'white' : 'rgba(255,255,255,0.7)',
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Your Message"
                                multiline
                                rows={6}
                                value={formData.message}
                                onChange={handleInputChange('message')}
                                error={!!errors.message}
                                helperText={errors.message}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                      borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255,255,255,0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff4d4d',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                  },
                                  '& .MuiFormHelperText-root': {
                                    color: '#ff4d4d',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                                  Attachments
                                </Typography>
                                <Button
                                  component="label"
                                  startIcon={<CloudUpload />}
                                  variant="outlined"
                                  sx={{
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    color: 'rgba(255,255,255,0.7)',
                                    '&:hover': {
                                      borderColor: '#ff4d4d',
                                      color: '#ff4d4d',
                                    },
                                  }}
                                >
                                  Upload Files
                                  <input
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={handleFileUpload}
                                  />
                                </Button>
                                {formData.attachments.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    {formData.attachments.map((file, index) => (
                                      <Chip
                                        key={index}
                                        label={file.name}
                                        onDelete={() => removeAttachment(index)}
                                        deleteIcon={<Delete />}
                                        variant="outlined"
                                        sx={{
                                          borderColor: 'rgba(255,255,255,0.3)',
                                          color: 'rgba(255,255,255,0.7)',
                                          mr: 1,
                                          mb: 1,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </motion.div>
                      )}

                      {/* Step 3: Review */}
                      {activeStep === 2 && (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Paper
                            sx={{
                              p: 3,
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
                              Review Your Message
                            </Typography>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Name
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                                  {formData.name}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Email
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                                  {formData.email}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Phone
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                                  {formData.phone || 'Not provided'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Company
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                                  {formData.company || 'Not provided'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Subject
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                                  {formData.subject}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Priority
                                </Typography>
                                <Chip
                                  label={priorities.find(p => p.value === formData.priority)?.label}
                                  color={priorities.find(p => p.value === formData.priority)?.color}
                                  size="small"
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  Message
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white', whiteSpace: 'pre-wrap' }}>
                                  {formData.message}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        </motion.div>
                      )}

                      {/* Navigation Buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                          onClick={handleBack}
                          disabled={activeStep === 0}
                          sx={{
                            color: 'rgba(255,255,255,0.7)',
                            '&:hover': {
                              color: '#ff4d4d',
                            },
                          }}
                        >
                          Back
                        </Button>

                        {activeStep === steps.length - 1 ? (
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <Schedule /> : <Send />}
                            sx={{
                              background: 'linear-gradient(135deg, #ff3333 0%, #cc0000 100%)',
                              px: 4,
                              py: 1.2,
                              borderRadius: 3,
                              fontWeight: 'bold',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #cc0000 0%, #ff3333 100%)',
                                boxShadow: '0 8px 25px rgba(255, 51, 51, 0.4)',
                              },
                            }}
                          >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                          </Button>
                        ) : (
                          <Button
                            onClick={handleNext}
                            variant="contained"
                            sx={{
                              background: 'linear-gradient(135deg, #ff3333 0%, #cc0000 100%)',
                              px: 4,
                              py: 1.2,
                              borderRadius: 3,
                              fontWeight: 'bold',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #cc0000 0%, #ff3333 100%)',
                                boxShadow: '0 8px 25px rgba(255, 51, 51, 0.4)',
                              },
                            }}
                          >
                            Next
                          </Button>
                        )}
                      </Box>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default EmailService;